<template>
  <div class="keypad" :class="{ 'keypad--disabled': disabled || locked }">
    <div class="pin-indicator" aria-label="Four digit PIN">
      <span
        v-for="circle in pinCodeCircles"
        :key="circle"
        class="pin-indicator__dot"
        :class="{ 'pin-indicator__dot--filled': pin.length >= circle }"
      >
        <v-icon v-if="pin.length >= circle" small color="white">mdi-check</v-icon>
      </span>
    </div>

    <div class="keypad-grid">
      <button
        v-for="num in numbers"
        :key="num"
        type="button"
        class="keypad-key"
        :disabled="disabled || locked"
        :aria-label="`Enter ${num}`"
        @pointerdown.stop.prevent="pointerMode && pressNumber(num)"
        @click.stop.prevent="!pointerMode && pressNumber(num)"
      >
        {{ num }}
      </button>

      <button
        type="button"
        class="keypad-key keypad-key--utility"
        :disabled="disabled || locked || pin.length === 0"
        aria-label="Delete last digit"
        @pointerdown.stop.prevent="pointerMode && deleteLast()"
        @click.stop.prevent="!pointerMode && deleteLast()"
      >
        <v-icon>mdi-backspace-outline</v-icon>
        <small>Delete</small>
      </button>

      <button
        type="button"
        class="keypad-key"
        :disabled="disabled || locked"
        aria-label="Enter 0"
        @pointerdown.stop.prevent="pointerMode && pressNumber(0)"
        @click.stop.prevent="!pointerMode && pressNumber(0)"
      >
        0
      </button>

      <button
        type="button"
        class="keypad-key keypad-key--utility keypad-key--clear"
        :disabled="disabled || locked || pin.length === 0"
        aria-label="Clear PIN"
        @pointerdown.stop.prevent="pointerMode && clearPin()"
        @click.stop.prevent="!pointerMode && clearPin()"
      >
        <v-icon>mdi-close-circle-outline</v-icon>
        <small>Clear</small>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'keypad-input',
  props: {
    selectedEmployee: {
      type: Object,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      pointerMode: "PointerEvent" in window,
      numbers: [1, 2, 3, 4, 5, 6, 7, 8, 9],
      pinCodeCircles: [1, 2, 3, 4],
      pin: "",
      locked: false,
    }
  },
  watch: {
    async pin() {
      if (this.pin.length === 4) {
        this.locked = true;
        this.$emit('pinValueSubmit', this.pin)
      }
    },
  },
  methods: {
    clearPin() {
      this.pin = ""
      this.locked = false
    },
    deleteLast() {
      if (this.disabled || this.locked) return
      this.pin = this.pin.slice(0, -1)
    },
    pressNumber(num) {
      if (this.disabled || this.locked || this.pin.length >= 4) return
      num = num.toString();
      this.pin = this.pin.concat(num).trim();
    },
  }
}
</script>

<style scoped>
.keypad {
  width: 100%;
  max-width: 520px;
  margin-top: clamp(18px, 3vh, 30px);
  user-select: none;
  -webkit-user-select: none;
}

.pin-indicator {
  display: flex;
  margin-bottom: clamp(18px, 3vh, 28px);
  justify-content: center;
  gap: 14px;
}

.pin-indicator__dot {
  display: grid;
  width: 42px;
  height: 42px;
  place-items: center;
  border: 2px solid #334155;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.035);
  transition: background 100ms ease, border-color 100ms ease, transform 100ms ease;
}

.pin-indicator__dot--filled {
  border-color: #3b82f6;
  background: #2563eb;
  transform: scale(1.05);
}

.keypad-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(82px, 1fr));
  gap: clamp(10px, 1.7vh, 16px);
}

.keypad-key {
  display: flex;
  min-height: clamp(72px, 10.5vh, 100px);
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 20px;
  background: rgba(15, 29, 48, 0.92);
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.18);
  color: #f8fafc;
  font-family: inherit;
  font-size: clamp(28px, 4.5vw, 40px);
  font-weight: 650;
  line-height: 1;
  outline: none;
  touch-action: manipulation;
  transition: background 80ms ease, border-color 80ms ease, box-shadow 80ms ease, transform 80ms ease;
  -webkit-tap-highlight-color: transparent;
}

.keypad-key:active {
  border-color: #60a5fa;
  background: rgba(37, 99, 235, 0.3);
  box-shadow: 0 2px 5px rgba(37, 99, 235, 0.16);
  transform: scale(0.95);
}

.keypad-key:focus-visible {
  border-color: #2563eb;
  box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.18);
}

.keypad-key--utility {
  flex-direction: column;
  gap: 5px;
  background: rgba(148, 163, 184, 0.08);
  color: #cbd5e1;
}

.keypad-key--utility .v-icon {
  color: currentColor;
}

.keypad-key--utility small {
  font-size: 11px;
  font-weight: 700;
}

.keypad-key--clear {
  border-color: rgba(251, 113, 133, 0.2);
  background: rgba(244, 63, 94, 0.1);
  color: #fda4af;
}

.keypad-key:disabled {
  cursor: default;
}

.keypad-key:disabled:not(.keypad-key--utility),
.keypad--disabled .keypad-key {
  opacity: 0.58;
}

@media (max-width: 600px) {
  .keypad {
    margin-top: 18px;
  }

  .keypad-grid {
    gap: 10px;
  }

  .keypad-key {
    min-height: clamp(68px, 10vh, 88px);
    border-radius: 16px;
  }
}

@media (max-height: 720px) {
  .pin-indicator {
    margin-bottom: 12px;
  }

  .pin-indicator__dot {
    width: 34px;
    height: 34px;
  }

  .keypad-grid {
    gap: 8px;
  }

  .keypad-key {
    min-height: 62px;
  }
}
</style>
